import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { cn } from '../lib/cn';
import { EmailEditor } from '../components/email-editor';
import {
  Mail,
  Send,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Users,
  RefreshCw,
  Filter,
  Loader2,
  X,
  Play,
  Settings,
  FileText,
  Plus,
  Edit,
  Trash2,
  Calendar,
  BarChart2,
  Image as ImageIcon,
} from 'lucide-react';

interface NotificationLog {
  id: string;
  type: string;
  subject: string | null;
  channel: string;
  status: string;
  sentAt: string;
  member: {
    firstName: string;
    lastName: string;
    email: string;
  } | null;
}

interface SchedulerStatus {
  isRunning: boolean;
  jobs: {
    name: string;
    nextRun: string | null;
    lastRun: string | null;
  }[];
}

interface Member {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  status: string;
}

interface Campaign {
  id: string;
  name: string;
  subject: string;
  bodyHtml: string;
  bodyText?: string;
  status: 'draft' | 'scheduled' | 'sending' | 'sent';
  scheduledFor: string | null;
  sentAt: string | null;
  totalRecipients: number;
  totalSent: number;
  totalOpens: number;
  totalClicks: number;
  recipientFilter?: { type: string; memberIds?: string[] };
  createdAt: string;
}

interface Template {
  id: string;
  name: string;
  subject: string;
  bodyHtml: string;
  bodyText?: string;
  type: string;
}

interface MediaItem {
  id: string;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  url: string;
  altText?: string;
}

type TabId = 'campaigns' | 'compose' | 'templates' | 'history' | 'scheduler';

const tabs: { id: TabId; name: string; icon: typeof Mail }[] = [
  { id: 'campaigns', name: 'Campaigns', icon: FileText },
  { id: 'compose', name: 'Quick Send', icon: Send },
  { id: 'templates', name: 'Templates', icon: Mail },
  { id: 'history', name: 'History', icon: Clock },
  { id: 'scheduler', name: 'Scheduler', icon: Settings },
];

export function CommunicationsPage() {
  const [activeTab, setActiveTab] = useState<TabId>('campaigns');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Communications</h1>
        <p className="mt-1 text-sm text-gray-500">
          Send emails and manage automated notifications
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'flex items-center gap-2 border-b-2 py-3 px-1 text-sm font-medium transition-colors',
                activeTab === tab.id
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              )}
            >
              <tab.icon className="h-4 w-4" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'campaigns' && <CampaignsTab />}
      {activeTab === 'compose' && <ComposeTab />}
      {activeTab === 'templates' && <TemplatesTab />}
      {activeTab === 'history' && <HistoryTab />}
      {activeTab === 'scheduler' && <SchedulerTab />}
    </div>
  );
}

function ComposeTab() {
  const queryClient = useQueryClient();
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [recipientFilter, setRecipientFilter] = useState('all');
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [showSuccess, setShowSuccess] = useState(false);

  const { data: members } = useQuery({
    queryKey: ['members-for-email'],
    queryFn: async () => {
      const response = await api.get<Member[]>('/members?limit=500');
      if (!response.success) throw new Error(response.error?.message);
      return response.data || [];
    },
  });

  const sendMutation = useMutation({
    mutationFn: async (data: { subject: string; message: string; memberIds?: string[]; filter?: string }) => {
      const response = await api.post('/notifications/send-bulk', data);
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    onSuccess: () => {
      setShowSuccess(true);
      setSubject('');
      setMessage('');
      setSelectedMembers([]);
      queryClient.invalidateQueries({ queryKey: ['notification-history'] });
      setTimeout(() => setShowSuccess(false), 5000);
    },
  });

  const testEmailMutation = useMutation({
    mutationFn: async (email: string) => {
      const response = await api.post('/notifications/test-email', { email });
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
  });

  const filteredMembers = members?.filter((m) => {
    if (!m.email) return false;
    if (recipientFilter === 'active') return m.status === 'active';
    return true;
  }) || [];

  const recipientCount = recipientFilter === 'selected'
    ? selectedMembers.length
    : filteredMembers.length;

  const handleSend = () => {
    if (!subject.trim() || !message.trim()) return;

    sendMutation.mutate({
      subject,
      message,
      ...(recipientFilter === 'selected'
        ? { memberIds: selectedMembers }
        : { filter: recipientFilter }),
    });
  };

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      {/* Compose Form */}
      <div className="lg:col-span-2 space-y-6">
        {showSuccess && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <p className="text-green-800">Email campaign sent successfully!</p>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Compose Email</h2>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subject *
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Email subject line"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Message *
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Write your message here..."
              rows={12}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              You can use basic formatting. Line breaks will be preserved.
            </p>
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              Sending to <span className="font-medium text-gray-900">{recipientCount}</span> recipients
            </p>
            <button
              onClick={handleSend}
              disabled={sendMutation.isPending || !subject.trim() || !message.trim() || recipientCount === 0}
              className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {sendMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  Send Email
                </>
              )}
            </button>
          </div>

          {sendMutation.isError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-700 text-sm">
              <AlertCircle className="h-4 w-4" />
              {sendMutation.error instanceof Error ? sendMutation.error.message : 'Failed to send'}
            </div>
          )}
        </div>

        {/* Test Email */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-sm font-medium text-gray-900 mb-3">Test Email Configuration</h3>
          <div className="flex gap-2">
            <input
              type="email"
              placeholder="test@example.com"
              id="test-email"
              className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
            <button
              onClick={() => {
                const input = document.getElementById('test-email') as HTMLInputElement;
                if (input.value) testEmailMutation.mutate(input.value);
              }}
              disabled={testEmailMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50"
            >
              {testEmailMutation.isPending ? 'Sending...' : 'Send Test'}
            </button>
          </div>
          {testEmailMutation.isSuccess && (
            <p className="mt-2 text-sm text-green-600">Test email sent successfully!</p>
          )}
          {testEmailMutation.isError && (
            <p className="mt-2 text-sm text-red-600">
              {testEmailMutation.error instanceof Error ? testEmailMutation.error.message : 'Failed to send test email'}
            </p>
          )}
        </div>
      </div>

      {/* Recipients Panel */}
      <div className="space-y-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recipients</h2>

          <div className="space-y-3">
            <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50">
              <input
                type="radio"
                name="recipients"
                value="all"
                checked={recipientFilter === 'all'}
                onChange={() => setRecipientFilter('all')}
                className="text-primary-600 focus:ring-primary-500"
              />
              <div>
                <p className="text-sm font-medium text-gray-900">All Members</p>
                <p className="text-xs text-gray-500">{members?.filter(m => m.email).length || 0} with email</p>
              </div>
            </label>

            <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50">
              <input
                type="radio"
                name="recipients"
                value="active"
                checked={recipientFilter === 'active'}
                onChange={() => setRecipientFilter('active')}
                className="text-primary-600 focus:ring-primary-500"
              />
              <div>
                <p className="text-sm font-medium text-gray-900">Active Members Only</p>
                <p className="text-xs text-gray-500">
                  {members?.filter(m => m.email && m.status === 'active').length || 0} members
                </p>
              </div>
            </label>

            <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50">
              <input
                type="radio"
                name="recipients"
                value="selected"
                checked={recipientFilter === 'selected'}
                onChange={() => setRecipientFilter('selected')}
                className="text-primary-600 focus:ring-primary-500"
              />
              <div>
                <p className="text-sm font-medium text-gray-900">Select Specific Members</p>
                <p className="text-xs text-gray-500">{selectedMembers.length} selected</p>
              </div>
            </label>
          </div>

          {recipientFilter === 'selected' && (
            <div className="mt-4 border-t border-gray-200 pt-4">
              <div className="max-h-64 overflow-y-auto space-y-1">
                {filteredMembers.map((member) => (
                  <label
                    key={member.id}
                    className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedMembers.includes(member.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedMembers([...selectedMembers, member.id]);
                        } else {
                          setSelectedMembers(selectedMembers.filter((id) => id !== member.id));
                        }
                      }}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">
                      {member.firstName} {member.lastName}
                    </span>
                  </label>
                ))}
              </div>
              {selectedMembers.length > 0 && (
                <button
                  onClick={() => setSelectedMembers([])}
                  className="mt-2 text-xs text-gray-500 hover:text-gray-700"
                >
                  Clear selection
                </button>
              )}
            </div>
          )}
        </div>

        {/* Quick Stats */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-sm font-medium text-gray-900 mb-3">Email Stats</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Total members</span>
              <span className="font-medium">{members?.length || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">With email</span>
              <span className="font-medium">{members?.filter(m => m.email).length || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Active with email</span>
              <span className="font-medium">{members?.filter(m => m.email && m.status === 'active').length || 0}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function HistoryTab() {
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(0);
  const limit = 25;

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['notification-history', typeFilter, page],
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: (page * limit).toString(),
      });
      if (typeFilter) params.append('type', typeFilter);
      const response = await api.get<NotificationLog[]>(`/notifications/log?${params}`);
      if (!response.success) throw new Error(response.error?.message);
      return {
        logs: response.data || [],
        pagination: (response as { pagination?: { total: number } }).pagination,
      };
    },
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'sent':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-400" />;
    }
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      yahrzeit_reminder: 'Yahrzeit Reminder',
      event_reminder: 'Event Reminder',
      donation_receipt: 'Donation Receipt',
      payment_receipt: 'Payment Receipt',
      bulk_email: 'Bulk Email',
      welcome: 'Welcome Email',
    };
    return labels[type] || type;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-400" />
          <select
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value);
              setPage(0);
            }}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            <option value="">All Types</option>
            <option value="yahrzeit_reminder">Yahrzeit Reminders</option>
            <option value="event_reminder">Event Reminders</option>
            <option value="donation_receipt">Donation Receipts</option>
            <option value="bulk_email">Bulk Emails</option>
          </select>
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {/* Logs Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Subject
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Recipient
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Sent At
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data?.logs.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                  No notifications found
                </td>
              </tr>
            ) : (
              data?.logs.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusIcon(log.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{getTypeLabel(log.type)}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-900 truncate max-w-xs block">
                      {log.subject || '-'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {log.member ? (
                      <span className="text-sm text-gray-900">
                        {log.member.firstName} {log.member.lastName}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(log.sentAt).toLocaleString()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {data?.pagination && data.pagination.total > limit && (
          <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 px-6 py-3">
            <p className="text-sm text-gray-500">
              Showing {page * limit + 1} to {Math.min((page + 1) * limit, data.pagination.total)} of{' '}
              {data.pagination.total}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 0}
                className="px-3 py-1 text-sm border rounded hover:bg-gray-100 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={(page + 1) * limit >= data.pagination.total}
                className="px-3 py-1 text-sm border rounded hover:bg-gray-100 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SchedulerTab() {
  const queryClient = useQueryClient();

  const { data: status, isLoading } = useQuery({
    queryKey: ['scheduler-status'],
    queryFn: async () => {
      const response = await api.get<SchedulerStatus>('/notifications/scheduler/status');
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const runJobMutation = useMutation({
    mutationFn: async (jobName: string) => {
      const response = await api.post(`/notifications/scheduler/run/${jobName}`);
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduler-status'] });
      queryClient.invalidateQueries({ queryKey: ['notification-history'] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Notification Scheduler</h2>
          <span
            className={cn(
              'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium',
              status?.isRunning
                ? 'bg-green-100 text-green-700'
                : 'bg-gray-100 text-gray-600'
            )}
          >
            <span className={cn('h-2 w-2 rounded-full', status?.isRunning ? 'bg-green-500' : 'bg-gray-400')} />
            {status?.isRunning ? 'Running' : 'Stopped'}
          </span>
        </div>

        <p className="text-sm text-gray-500 mb-6">
          The scheduler automatically sends yahrzeit reminders, event notifications, and other
          automated communications. Jobs run on schedule in production.
        </p>

        {/* Jobs List */}
        <div className="space-y-3">
          {status?.jobs.map((job) => (
            <div
              key={job.name}
              className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
            >
              <div>
                <p className="font-medium text-gray-900">{formatJobName(job.name)}</p>
                <div className="text-sm text-gray-500 space-x-4">
                  {job.nextRun && (
                    <span>Next: {new Date(job.nextRun).toLocaleString()}</span>
                  )}
                  {job.lastRun && (
                    <span>Last: {new Date(job.lastRun).toLocaleString()}</span>
                  )}
                </div>
              </div>
              <button
                onClick={() => runJobMutation.mutate(job.name)}
                disabled={runJobMutation.isPending}
                className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-primary-600 hover:bg-primary-50 rounded-lg disabled:opacity-50"
              >
                <Play className="h-4 w-4" />
                Run Now
              </button>
            </div>
          ))}
        </div>

        {runJobMutation.isError && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-700 text-sm">
            <AlertCircle className="h-4 w-4" />
            {runJobMutation.error instanceof Error ? runJobMutation.error.message : 'Failed to run job'}
          </div>
        )}

        {runJobMutation.isSuccess && (
          <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2 text-green-700 text-sm">
            <CheckCircle className="h-4 w-4" />
            Job triggered successfully
          </div>
        )}
      </div>

      {/* Info Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <h3 className="text-sm font-medium text-blue-900 mb-2">About Automated Notifications</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Yahrzeit reminders are sent based on member preferences (7 days and 1 day before)</li>
          <li>• Event reminders are sent 24 hours before registered events</li>
          <li>• Donation receipts are sent immediately after successful donations</li>
          <li>• Jobs only run in production mode to prevent duplicate sends during development</li>
        </ul>
      </div>
    </div>
  );
}

function formatJobName(name: string): string {
  const names: Record<string, string> = {
    'yahrzeit-reminders': 'Yahrzeit Reminders',
    'event-reminders': 'Event Reminders',
    'membership-renewals': 'Membership Renewals',
    yahrzeitReminders: 'Yahrzeit Reminders',
    eventReminders: 'Event Reminders',
    membershipRenewals: 'Membership Renewals',
  };
  return names[name] || name.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function CampaignsTab() {
  const queryClient = useQueryClient();
  const [showEditor, setShowEditor] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);

  const { data: campaigns, isLoading } = useQuery({
    queryKey: ['campaigns'],
    queryFn: async () => {
      const response = await api.get<Campaign[]>('/notifications/campaigns');
      if (!response.success) throw new Error(response.error?.message);
      return response.data || [];
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete(`/notifications/campaigns/${id}`);
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });

  const sendMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.post(`/notifications/campaigns/${id}/send`);
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });

  const getStatusBadge = (status: string) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-700',
      scheduled: 'bg-blue-100 text-blue-700',
      sending: 'bg-yellow-100 text-yellow-700',
      sent: 'bg-green-100 text-green-700',
    };
    return (
      <span className={cn('px-2 py-1 text-xs font-medium rounded-full', styles[status as keyof typeof styles] || styles.draft)}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (showEditor) {
    return (
      <CampaignEditor
        campaign={editingCampaign}
        onClose={() => {
          setShowEditor(false);
          setEditingCampaign(null);
        }}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500">
          Create and manage email campaigns with scheduling and analytics.
        </p>
        <button
          onClick={() => setShowEditor(true)}
          className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
        >
          <Plus className="h-4 w-4" />
          New Campaign
        </button>
      </div>

      {campaigns?.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900">No campaigns yet</h3>
          <p className="text-gray-500 mt-1">Create your first email campaign to get started.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campaign</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recipients</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sent</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {campaigns?.map((campaign) => (
                <tr key={campaign.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-medium text-gray-900">{campaign.name}</p>
                      <p className="text-sm text-gray-500">{campaign.subject}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">{getStatusBadge(campaign.status)}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{campaign.totalRecipients}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{campaign.totalSent}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {campaign.sentAt
                      ? new Date(campaign.sentAt).toLocaleDateString()
                      : campaign.scheduledFor
                      ? `Scheduled: ${new Date(campaign.scheduledFor).toLocaleDateString()}`
                      : new Date(campaign.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      {campaign.status === 'draft' && (
                        <>
                          <button
                            onClick={() => {
                              setEditingCampaign(campaign);
                              setShowEditor(true);
                            }}
                            className="p-1 text-gray-400 hover:text-gray-600"
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => {
                              if (confirm('Send this campaign now?')) {
                                sendMutation.mutate(campaign.id);
                              }
                            }}
                            disabled={sendMutation.isPending}
                            className="p-1 text-green-500 hover:text-green-700"
                            title="Send Now"
                          >
                            <Send className="h-4 w-4" />
                          </button>
                        </>
                      )}
                      {campaign.status === 'scheduled' && (
                        <button
                          onClick={() => {
                            setEditingCampaign(campaign);
                            setShowEditor(true);
                          }}
                          className="p-1 text-gray-400 hover:text-gray-600"
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                      )}
                      {(campaign.status === 'draft' || campaign.status === 'scheduled') && (
                        <button
                          onClick={() => {
                            if (confirm('Delete this campaign?')) {
                              deleteMutation.mutate(campaign.id);
                            }
                          }}
                          disabled={deleteMutation.isPending}
                          className="p-1 text-red-400 hover:text-red-600"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                      {campaign.status === 'sent' && (
                        <button
                          className="p-1 text-gray-400 hover:text-gray-600"
                          title="View Stats"
                        >
                          <BarChart2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CampaignEditor({ campaign, onClose }: { campaign: Campaign | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(campaign?.name || '');
  const [subject, setSubject] = useState(campaign?.subject || '');
  const [bodyHtml, setBodyHtml] = useState(campaign?.bodyHtml || '');
  const [blocksJson, setBlocksJson] = useState('');
  const [recipientType, setRecipientType] = useState<'all' | 'active' | 'selected'>(
    (campaign?.recipientFilter?.type as 'all' | 'active' | 'selected') || 'active'
  );
  const [scheduledFor, setScheduledFor] = useState(
    campaign?.scheduledFor ? new Date(campaign.scheduledFor).toISOString().slice(0, 16) : ''
  );

  const { data: members } = useQuery({
    queryKey: ['members-for-campaign'],
    queryFn: async () => {
      const response = await api.get<Member[]>('/members?limit=500');
      if (!response.success) throw new Error(response.error?.message);
      return (response.data || []).filter(m => m.email);
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (data: Partial<Campaign>) => {
      if (campaign) {
        const response = await api.patch(`/notifications/campaigns/${campaign.id}`, data);
        if (!response.success) throw new Error(response.error?.message);
        return response.data;
      } else {
        const response = await api.post('/notifications/campaigns', data);
        if (!response.success) throw new Error(response.error?.message);
        return response.data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      onClose();
    },
  });

  const recipientCount = recipientType === 'active'
    ? members?.filter(m => m.status === 'active').length || 0
    : members?.length || 0;

  const handleEditorChange = useCallback((value: { blocksJson: string; html: string; text: string }) => {
    setBodyHtml(value.html);
    setBlocksJson(value.blocksJson);
  }, []);

  const handleSave = (sendNow = false) => {
    saveMutation.mutate({
      name,
      subject,
      bodyHtml,
      bodyText: blocksJson || undefined,
      recipientFilter: { type: recipientType },
      scheduledFor: sendNow ? undefined : (scheduledFor || undefined),
    });
  };

  // Determine initial editor value: prefer blocksJson stored in bodyText, fallback to bodyHtml
  const editorInitialValue = campaign?.bodyText && campaign.bodyText.startsWith('[')
    ? campaign.bodyText
    : campaign?.bodyHtml || '';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">
          {campaign ? 'Edit Campaign' : 'New Campaign'}
        </h2>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., December Newsletter"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email Subject *</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Subject line for the email"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email Content *</label>
              <EmailEditor
                value={editorInitialValue}
                onChange={handleEditorChange}
              />
            </div>
          </div>

          {saveMutation.isError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-700 text-sm">
              <AlertCircle className="h-4 w-4" />
              {saveMutation.error instanceof Error ? saveMutation.error.message : 'Failed to save'}
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-sm font-medium text-gray-900 mb-4">Recipients</h3>
            <div className="space-y-3">
              <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="recipients"
                  checked={recipientType === 'active'}
                  onChange={() => setRecipientType('active')}
                  className="text-primary-600 focus:ring-primary-500"
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">Active Members</p>
                  <p className="text-xs text-gray-500">{members?.filter(m => m.status === 'active').length || 0} members</p>
                </div>
              </label>

              <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="recipients"
                  checked={recipientType === 'all'}
                  onChange={() => setRecipientType('all')}
                  className="text-primary-600 focus:ring-primary-500"
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">All Members</p>
                  <p className="text-xs text-gray-500">{members?.length || 0} with email</p>
                </div>
              </label>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-sm font-medium text-gray-900 mb-4">Schedule (Optional)</h3>
            <input
              type="datetime-local"
              value={scheduledFor}
              onChange={(e) => setScheduledFor(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
            <p className="mt-2 text-xs text-gray-500">
              Leave empty to save as draft, or set a date to schedule.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-3">
            <button
              onClick={() => handleSave(false)}
              disabled={saveMutation.isPending || !name || !subject || !bodyHtml}
              className="w-full flex items-center justify-center gap-2 rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-50"
            >
              {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
              {scheduledFor ? 'Schedule Campaign' : 'Save as Draft'}
            </button>
            <p className="text-center text-xs text-gray-500">
              Will send to {recipientCount} recipients
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function TemplatesTab() {
  const queryClient = useQueryClient();
  const [showEditor, setShowEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);

  const { data: templates, isLoading } = useQuery({
    queryKey: ['email-templates'],
    queryFn: async () => {
      const response = await api.get<Template[]>('/notifications/templates');
      if (!response.success) throw new Error(response.error?.message);
      return response.data || [];
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete(`/notifications/templates/${id}`);
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
    },
  });

  if (showEditor) {
    return (
      <TemplateEditor
        template={editingTemplate}
        onClose={() => {
          setShowEditor(false);
          setEditingTemplate(null);
        }}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500">
          Create reusable email templates for campaigns and automated messages.
        </p>
        <button
          onClick={() => setShowEditor(true)}
          className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
        >
          <Plus className="h-4 w-4" />
          New Template
        </button>
      </div>

      {templates?.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <Mail className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900">No templates yet</h3>
          <p className="text-gray-500 mt-1">Create your first email template to get started.</p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {templates?.map((template) => (
            <div key={template.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-medium text-gray-900">{template.name}</h3>
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded mt-1 inline-block">
                    {template.type}
                  </span>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => {
                      setEditingTemplate(template);
                      setShowEditor(true);
                    }}
                    className="p-1 text-gray-400 hover:text-gray-600"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => {
                      if (confirm('Delete this template?')) {
                        deleteMutation.mutate(template.id);
                      }
                    }}
                    className="p-1 text-red-400 hover:text-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <p className="text-sm text-gray-600 truncate">{template.subject}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TemplateEditor({ template, onClose }: { template: Template | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(template?.name || '');
  const [subject, setSubject] = useState(template?.subject || '');
  const [bodyHtml, setBodyHtml] = useState(template?.bodyHtml || '');
  const [blocksJson, setBlocksJson] = useState('');
  const [type, setType] = useState(template?.type || 'custom');

  const saveMutation = useMutation({
    mutationFn: async (data: Partial<Template>) => {
      if (template) {
        const response = await api.patch(`/notifications/templates/${template.id}`, data);
        if (!response.success) throw new Error(response.error?.message);
        return response.data;
      } else {
        const response = await api.post('/notifications/templates', data);
        if (!response.success) throw new Error(response.error?.message);
        return response.data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      onClose();
    },
  });

  const handleEditorChange = useCallback((value: { blocksJson: string; html: string; text: string }) => {
    setBodyHtml(value.html);
    setBlocksJson(value.blocksJson);
  }, []);

  // Determine initial editor value: prefer blocksJson stored in bodyText, fallback to bodyHtml
  const editorInitialValue = template?.bodyText && template.bodyText.startsWith('[')
    ? template.bodyText
    : template?.bodyHtml || '';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">
          {template ? 'Edit Template' : 'New Template'}
        </h2>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Template Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Welcome Email"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="custom">Custom</option>
              <option value="welcome">Welcome</option>
              <option value="yahrzeit">Yahrzeit</option>
              <option value="billing">Billing</option>
              <option value="event">Event</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Subject Line *</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Email subject"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email Body *</label>
          <EmailEditor
            value={editorInitialValue}
            onChange={handleEditorChange}
          />
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
          >
            Cancel
          </button>
          <button
            onClick={() => saveMutation.mutate({ name, subject, bodyHtml, bodyText: blocksJson || undefined, type })}
            disabled={saveMutation.isPending || !name || !subject || !bodyHtml}
            className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
            Save Template
          </button>
        </div>

        {saveMutation.isError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-700 text-sm">
            <AlertCircle className="h-4 w-4" />
            {saveMutation.error instanceof Error ? saveMutation.error.message : 'Failed to save'}
          </div>
        )}
      </div>
    </div>
  );
}

function EmailImagePicker({
  onSelect,
  onClose,
}: {
  onSelect: (url: string, alt: string) => void;
  onClose: () => void;
}) {
  const { data: mediaItems, isLoading } = useQuery({
    queryKey: ['media-images-for-email'],
    queryFn: async () => {
      const response = await api.get<{ data: MediaItem[] }>('/media?mimeType=image&limit=100');
      if (!response.success) throw new Error(response.error?.message);
      return response.data?.data || [];
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold text-gray-900">Insert Image</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
            </div>
          ) : mediaItems?.length === 0 ? (
            <div className="text-center py-12">
              <ImageIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No images uploaded yet.</p>
              <p className="text-sm text-gray-400 mt-1">Go to Media Library to upload images first.</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-4">
              {mediaItems?.map((item) => (
                <button
                  key={item.id}
                  onClick={() => onSelect(item.url, item.altText || item.originalName)}
                  className="aspect-square bg-gray-100 rounded-lg overflow-hidden border-2 border-transparent hover:border-primary-500 focus:border-primary-500 focus:outline-none transition-colors group relative"
                >
                  <img
                    src={item.url}
                    alt={item.altText || item.originalName}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                    <span className="opacity-0 group-hover:opacity-100 text-white text-xs font-medium bg-black/50 px-2 py-1 rounded">
                      Insert
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="p-4 border-t bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
